/** BroadcastArchive · /broadcasts · public archive of every talk show */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const gold = "#FFD700";
const teal = "#00FFCC";

export default function BroadcastArchive() {
  const [shows, setShows] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/radio/archive`)
      .then((r) => r.json())
      .then((d) => setShows(d.shows || []))
      .catch(() => setShows([]));
  }, []);

  useEffect(() => {
    const a = audioRef.current;
    const onEnd = () => setPlayingId(null);
    a.addEventListener("ended", onEnd);
    return () => { a.removeEventListener("ended", onEnd); a.pause(); };
  }, []);

  const toggle = (id) => {
    const a = audioRef.current;
    if (playingId === id) {
      a.pause();
      setPlayingId(null);
      return;
    }
    a.src = `${API}/radio/stream/${id}`;
    a.play().catch(() => {});
    setPlayingId(id);
  };

  const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  const fmtDur = (s) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`;

  return (
    <main className="min-h-screen px-4 py-12 max-w-[860px] mx-auto" data-testid="broadcast-archive-page" style={{ color: "#e0e0e0" }}>
      <audio ref={audioRef} preload="none" />
      <p className="text-center font-mono uppercase tracking-[0.26em] mb-2" style={{ fontSize: 11, color: teal }}>
        The Sovereign Pulse · Federation Frequency
      </p>
      <h1 className="font-serif text-center mb-3" style={{ fontSize: "clamp(30px,4.5vw,44px)", color: gold, textShadow: "0 0 22px rgba(255,215,0,0.4)" }}>
        Broadcast Archive
      </h1>
      <p className="text-center font-serif mb-8" style={{ fontSize: 16, color: "rgba(224,224,224,0.75)" }}>
        Every talk show ever aired on NextXus Radio — press play on any transmission.
      </p>

      <div className="flex items-center justify-center gap-3 flex-wrap mb-10">
        <Link
          to="/radio"
          data-testid="archive-live-link"
          className="font-mono uppercase tracking-[0.22em] nx-gold-link"
          style={{ padding: "10px 16px", fontSize: 12, color: gold, background: "#111118", border: `1px solid ${gold}`, borderRadius: 8, textDecoration: "none" }}
        >
          ◉ Listen Live
        </Link>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/radio/rss`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="archive-rss-link"
          className="font-mono uppercase tracking-[0.22em] nx-gold-link"
          style={{ padding: "10px 16px", fontSize: 12, color: teal, background: "#111118", border: `1px solid ${teal}`, borderRadius: 8, textDecoration: "none" }}
        >
          ⧉ Podcast RSS Feed
        </a>
      </div>

      <section className="p-5" style={{ background: "#111118", border: "1px solid rgba(255,215,0,0.3)", borderRadius: 14 }} data-testid="archive-list">
        {shows === null && <p className="font-mono text-center py-6" style={{ fontSize: 13, color: "rgba(224,224,224,0.5)" }}>Tuning the archive…</p>}
        {shows?.length === 0 && (
          <p className="font-mono text-center py-6" style={{ fontSize: 13, color: "rgba(224,224,224,0.5)" }}>
            No broadcasts archived yet — the first talk show will appear here.
          </p>
        )}
        {shows?.map((s) => (
          <div key={s.id} className="flex items-center gap-4 py-3" style={{ borderBottom: "1px dashed rgba(255,215,0,0.15)" }}>
            {s.artwork ? (
              <img src={`${API}/radio/artwork/${s.id}`} alt={s.title} style={{ width: 52, height: 52, objectFit: "cover", borderRadius: 8, border: "1px solid rgba(255,215,0,0.4)" }} />
            ) : (
              <div className="flex items-center justify-center" style={{ width: 52, height: 52, borderRadius: 8, border: "1px solid rgba(0,255,204,0.3)", background: "#0a0a0f", color: teal, fontSize: 20 }} aria-hidden="true">◉</div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-serif truncate" style={{ fontSize: 17, color: playingId === s.id ? gold : "#e0e0e0" }}>{s.title}</p>
              <p className="font-mono uppercase tracking-[0.18em]" style={{ fontSize: 10, color: "rgba(224,224,224,0.5)" }}>
                {fmtDate(s.uploaded_at)} · {fmtDur(s.duration_sec)}
              </p>
            </div>
            <button
              type="button"
              data-testid={`archive-play-${s.id}`}
              onClick={() => toggle(s.id)}
              aria-label={playingId === s.id ? `Pause ${s.title}` : `Play ${s.title}`}
              className="font-mono uppercase tracking-[0.2em]"
              style={{
                minHeight: 44, minWidth: 44, padding: "0 18px", fontSize: 12, borderRadius: 999, cursor: "pointer",
                color: playingId === s.id ? "#0a0a0f" : gold,
                background: playingId === s.id ? "linear-gradient(135deg,#D4AF37,#FFD700)" : "transparent",
                border: `1px solid ${gold}`,
              }}
            >
              {playingId === s.id ? "❚❚" : "▶"}
            </button>
          </div>
        ))}
      </section>
    </main>
  );
}
